import type { ComponentProps } from 'react';
import { cn } from '../lib/cn.ts';

export type TextVariant =
  | 'display'
  | 'title'
  | 'heading'
  | 'subheading'
  | 'body'
  | 'bodyStrong'
  | 'caption'
  | 'mono';

export type TextTone = 'default' | 'muted' | 'brand' | 'danger' | 'success';
export type TextElement = 'p' | 'span' | 'div' | 'h1' | 'h2' | 'h3' | 'h4';

const variants: Record<TextVariant, string> = {
  display: 'font-display text-3xl font-bold tracking-tight',
  title: 'font-display text-2xl font-bold tracking-tight',
  heading: 'font-display text-xl font-bold tracking-tight',
  subheading: 'font-display text-lg font-bold tracking-tight',
  body: 'text-md',
  bodyStrong: 'text-md font-semibold',
  caption: 'text-sm',
  /** The product's overline: mono, upper case, tracked out. */
  // The one place the mono face is deliberate: an identifier, a sha, a
  // machine-written value. The product never uses it for a label — see
  // Overline, which used to and should not have.
  mono: 'font-mono text-2xs tracking-wider uppercase',
};

const tones: Record<TextTone, string> = {
  default: 'text-ink',
  muted: 'text-ink-muted',
  brand: 'text-brand-800',
  danger: 'text-danger-text',
  success: 'text-success-text',
};

/**
 * The element each variant renders when the caller does not say. A heading that
 * *looks* like a heading should also *be* one: without this every mockup is a
 * wall of <p>, and the first accessibility pass on the real screen has to
 * rediscover the hierarchy the designer already expressed.
 */
const defaultElements: Record<TextVariant, TextElement> = {
  display: 'h1',
  title: 'h2',
  heading: 'h3',
  subheading: 'h4',
  body: 'p',
  bodyStrong: 'p',
  caption: 'p',
  mono: 'span',
};

export interface TextProps extends ComponentProps<'p'> {
  variant?: TextVariant;
  tone?: TextTone;
  as?: TextElement;
  truncate?: boolean;
}

export function Text({
  variant = 'body',
  tone = 'default',
  as,
  truncate = false,
  className,
  ...props
}: TextProps) {
  const Component = as ?? defaultElements[variant];

  return (
    <Component
      className={cn(variants[variant], tones[tone], truncate && 'truncate', className)}
      {...props}
    />
  );
}
